import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, useState } from 'react';

import { cn } from '../lib/utils';
import { Tooltip } from '../Tooltip/Tooltip';

interface CopyContentProps {
  content: string;
  children?: ReactNode;
  iconTimeout?: number;
  className?: string;
  buttonClassName?: string;
  'data-testid'?: string;
}

function CopyContent({
  content,
  children,
  iconTimeout = 2000,
  className,
  buttonClassName,
  'data-testid': testId
}: CopyContentProps) {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), iconTimeout);
    });
  };

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} data-testid={testId}>
      <span className="min-w-0">{children ?? content}</span>
      <Tooltip content={isCopied ? 'Copied!' : 'Copy'}>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={isCopied ? 'Copied' : 'Copy content'}
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded text-uniform-gray-500',
            'transition-colors hover:text-green-600 dark:hover:text-green-400',
            '[outline:none] focus-visible:ring-1 focus-visible:ring-current',
            isCopied && 'text-green-600 dark:text-green-400',
            buttonClassName
          )}>
          <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} className="h-3.5 w-3.5" />
        </button>
      </Tooltip>
    </span>
  );
}

export { CopyContent };
export type { CopyContentProps };
